import axiosInstance from "@/config/axios";
import { emptyPaymentDelivery, PAYMENT_DELIVERY_EMPTY } from "@/config/sellerProfile/paymentDeliveryCompleteness";
import { emptyStoreInfo, STORE_INFO_EMPTY } from "@/config/sellerProfile/storeInfoCompleteness";
import {
    emptyStoreLocations,
    STORE_LOCATIONS_SECTION_ID,
} from "@/config/sellerProfile/storeLocationsCompleteness";

const PAYMENT_DELIVERY_URL = "/v1/seller-profile/payment-delivery";
const STORE_INFO_URL = "/v1/seller-profile/store-info";
const STORE_LOCATIONS_URL = "/v1/seller-profile/store-locations";
const COMPLETENESS_URL = "/v1/seller-profile/completeness";

function mergeSection(emptyPayloads, sectionId, section) {
    const emptyPayload = emptyPayloads[sectionId]?.() || {};

    return {
        payload: { ...emptyPayload, ...(section?.payload || {}) },
        isComplete: Boolean(section?.isComplete),
    };
}

function mergeProfile(emptyFactory, emptyPayloads, raw) {
    const empty = emptyFactory();

    if (!raw || typeof raw !== "object") {
        return empty;
    }

    return Object.fromEntries(
        Object.entries(empty).map(([sectionId, defaults]) => [
            sectionId,
            mergeSection(emptyPayloads, sectionId, raw[sectionId] || defaults),
        ])
    );
}

export async function getPaymentDelivery(shopId) {
    if (!shopId) {
        return emptyPaymentDelivery();
    }

    const { data } = await axiosInstance.get(PAYMENT_DELIVERY_URL);

    return mergeProfile(emptyPaymentDelivery, PAYMENT_DELIVERY_EMPTY, data);
}

export async function getStoreInfo(shopId) {
    if (!shopId) {
        return emptyStoreInfo();
    }

    const { data } = await axiosInstance.get(STORE_INFO_URL);

    return mergeProfile(emptyStoreInfo, STORE_INFO_EMPTY, data);
}

export async function getCompleteness(shopId) {
    if (!shopId) {
        return {
            sections: {},
            completeCount: 0,
            totalSlots: 0,
            paymentDeliveryComplete: false,
            storeInfoComplete: false,
            storeLocationsComplete: false,
        };
    }

    const { data } = await axiosInstance.get(COMPLETENESS_URL);

    return data;
}

export async function updateSection(shopId, sectionId, payload) {
    if (!shopId) {
        throw new Error("No shop selected");
    }

    const { data } = await axiosInstance.put(`${PAYMENT_DELIVERY_URL}/${sectionId}`, payload);

    return mergeSection(PAYMENT_DELIVERY_EMPTY, sectionId, data);
}

export async function updateStoreInfoSection(shopId, sectionId, payload) {
    if (!shopId) {
        throw new Error("No shop selected");
    }

    const { data } = await axiosInstance.put(`${STORE_INFO_URL}/${sectionId}`, payload);

    return mergeSection(STORE_INFO_EMPTY, sectionId, data);
}

export async function getStoreLocations(shopId, page = 1) {
    if (!shopId) {
        return emptyStoreLocations();
    }

    const { data } = await axiosInstance.get(STORE_LOCATIONS_URL, {
        params: { page },
    });

    const empty = emptyStoreLocations();

    return {
        [STORE_LOCATIONS_SECTION_ID]: {
            payload: {
                ...empty[STORE_LOCATIONS_SECTION_ID].payload,
                ...(data?.[STORE_LOCATIONS_SECTION_ID]?.payload || {}),
            },
            isComplete: Boolean(data?.[STORE_LOCATIONS_SECTION_ID]?.isComplete),
        },
    };
}

function applyLocationSection(data) {
    const empty = emptyStoreLocations()[STORE_LOCATIONS_SECTION_ID];

    return {
        payload: {
            ...empty.payload,
            ...(data?.payload || {}),
        },
        isComplete: Boolean(data?.isComplete),
    };
}

export async function updateStoreLocationsSection(shopId, sectionId, payload) {
    if (!shopId) {
        throw new Error("No shop selected");
    }

    const { data } = await axiosInstance.put(`${STORE_LOCATIONS_URL}/${sectionId}`, payload);

    return applyLocationSection(data);
}

export async function createStoreLocation(shopId, payload, page = 1) {
    if (!shopId) {
        throw new Error("No shop selected");
    }

    const { data } = await axiosInstance.post(`${STORE_LOCATIONS_URL}/locations`, payload, {
        params: { page },
    });

    return applyLocationSection(data);
}

export async function updateStoreLocation(shopId, id, payload, page = 1) {
    if (!shopId) {
        throw new Error("No shop selected");
    }

    const { data } = await axiosInstance.put(`${STORE_LOCATIONS_URL}/locations/${id}`, payload, {
        params: { page },
    });

    return applyLocationSection(data);
}

export async function deleteStoreLocation(shopId, id, page = 1) {
    if (!shopId) {
        throw new Error("No shop selected");
    }

    const { data } = await axiosInstance.delete(`${STORE_LOCATIONS_URL}/locations/${id}`, {
        params: { page },
    });

    return applyLocationSection(data);
}

export async function duplicateStoreLocation(shopId, id, page = 1) {
    if (!shopId) {
        throw new Error("No shop selected");
    }

    const { data } = await axiosInstance.post(
        `${STORE_LOCATIONS_URL}/locations/${id}/duplicate`,
        {},
        { params: { page } }
    );

    return applyLocationSection(data);
}
